// RightPanelProxyAPIManager.js
// Manager for Company Profile, Stock Performance, and Financials data
class RightPanelProxyAPIManager {
    constructor() {
        this.isInitialized = false;
        this.baseUrl = 'https://api.palosverdestech.com/tickers/details/';
        
        // Use local proxy to avoid CORS issues
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;
        const port = window.location.port || (protocol === 'https:' ? '443' : '80');
        this.proxyBaseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}/proxy-pvtech`;
        this.useProxy = false;
        
        // Cache for API responses
        this.cache = {
            profile: new Map(),
            performance: new Map(),
            financials: new Map()
        };
        
        // Cache TTL (Time To Live) in milliseconds (default: 1 minute)
        this.cacheTTL = 1 * 60 * 1000;
    }

    async initialize() {
        // No API keys or config needed for the proxy
        this.isInitialized = true;
    }

    static async create() {
        if (!RightPanelProxyAPIManager.instance) {
            const instance = new RightPanelProxyAPIManager();
            await instance.initialize();
            RightPanelProxyAPIManager.instance = instance;
        }
        return RightPanelProxyAPIManager.instance;
    }

    /**
     * Internal method for fetching data from proxy API
     * @param {string} endpoint - API endpoint path
     * @param {string} ticker - Stock ticker symbol
     * @returns {Promise<Object>} - Parsed JSON response
     */
    async fetchProxyGET(endpoint, ticker) {
        try {
            if (!this.isInitialized) {
                throw new Error('RightPanelProxyAPIManager not initialized.');
            }

            const targetUrl = `${this.baseUrl}${endpoint}/${ticker}`;
            
            let url;
            if (this.useProxy) {
                // Use local proxy to avoid CORS issues
                url = `${this.proxyBaseUrl}?url=${encodeURIComponent(targetUrl)}`;
                console.log(`📡 Proxy fetch (via local proxy): ${targetUrl}`);
            } else {
                url = targetUrl;
                console.log(`📡 Proxy fetch (direct): ${url}`);
            }

            const response = await fetch(url, { method: 'GET' });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const json = await response.json();
            return json;
        } catch (err) {
            console.error(`❌ Proxy fetch failed for ${endpoint}/${ticker}: ${err.message}`);
            throw err;
        }
    }

    /**
     * Get cached data if available and not expired
     * @param {string} type - Cache type ('profile', 'performance', 'financials')
     * @param {string} ticker - Stock ticker symbol
     * @returns {Object|null} - Cached data or null if not found/expired
     */
    getCachedData(type, ticker) {
        const cacheKey = `${ticker}`;
        const cached = this.cache[type].get(cacheKey);
        
        if (cached && (Date.now() - cached.timestamp) < this.cacheTTL) {
            console.log(`✅ Using cached ${type} data for ${ticker}`);
            return cached.data;
        }
        
        if (cached) {
            // Remove expired cache
            this.cache[type].delete(cacheKey);
        }
        
        return null;
    }

    /**
     * Store data in cache
     * @param {string} type - Cache type ('profile', 'performance', 'financials')
     * @param {string} ticker - Stock ticker symbol
     * @param {Object} data - Data to cache
     */
    setCachedData(type, ticker, data) {
        const cacheKey = `${ticker}`;
        this.cache[type].set(cacheKey, {
            data: data,
            timestamp: Date.now()
        });
    }

    /**
     * Clear cache for a specific type and ticker, or all cache
     * @param {string} type - Cache type ('profile', 'performance', 'financials') or 'all'
     * @param {string} ticker - Stock ticker symbol (optional if type is 'all')
     */
    clearCache(type = 'all', ticker = null) {
        if (type === 'all') {
            this.cache.profile.clear();
            this.cache.performance.clear();
            this.cache.financials.clear();
            console.log('🗑️ Cleared all cache');
        } else if (this.cache[type] && ticker) {
            this.cache[type].delete(ticker);
            console.log(`🗑️ Cleared ${type} cache for ${ticker}`);
        }
    }

    /**
     * Fetch Company Profile data
     * @param {string} ticker - Stock ticker symbol
     * @returns {Promise<Object>} - Normalized company profile data
     */
    async fetchCompanyProfile(ticker) {
        try {
            // Check cache first
            const cached = this.getCachedData('profile', ticker);
            if (cached) {
                return cached;
            }

            const response = await this.fetchProxyGET('companyprofile-v2', ticker);
            
            console.log(`📦 Raw API response for ${ticker}:`, response);
            
            // Handle API response format: {ok: true, data: {...}} or direct data object
            const data = response.data || response;
            
            console.log(`📦 Extracted data for ${ticker}:`, data);
            
            // Normalize address - can be object or string
            let addressString = '';
            if (data.address) {
                if (typeof data.address === 'object') {
                    // Format address object: {line1, city, state, zip, country}
                    const addr = data.address;
                    const parts = [
                        addr.line1,
                        addr.city,
                        addr.state,
                        addr.zip,
                        addr.country
                    ].filter(Boolean);
                    addressString = parts.join(', ');
                } else {
                    addressString = data.address;
                }
            } else if (data.headquarters) {
                addressString = typeof data.headquarters === 'object' 
                    ? Object.values(data.headquarters).filter(Boolean).join(', ')
                    : data.headquarters;
            }
            
            // Normalize fields
            const normalized = {
                companyName: data.companyName || data.company_name || data.name || '',
                logo: data.logoUrl || data.logo || data.logo_url || '',
                ticker: data.ticker || ticker,
                description: data.description || data.summary || '',
                address: addressString,
                sector: data.sector || '',
                industry: data.industry || '',
                employees: data.employees !== null && data.employees !== undefined 
                    ? data.employees 
                    : (data.employee_count !== null && data.employee_count !== undefined ? data.employee_count : null),
                exchange: data.exchange || data.stock_exchange || '',
                type: data.type || data.stock_type || ''
            };

            // Cache the normalized data
            this.setCachedData('profile', ticker, normalized);

            console.log(`✅ Company profile fetched and normalized for ${ticker}:`, normalized);
            return normalized;
        } catch (err) {
            console.error(`❌ Failed to fetch company profile for ${ticker}: ${err.message}`);
            return null;
        }
    }

    /**
     * Fetch Stock Performance data
     * @param {string} ticker - Stock ticker symbol
     * @returns {Promise<Object>} - Normalized stock performance data
     */
    async fetchStockPerformance(ticker) {
        try {
            // Check cache first
            const cached = this.getCachedData('performance', ticker);
            if (cached) {
                return cached;
            }

            const response = await this.fetchProxyGET('stockperf-v2', ticker);
            
            console.log(`📦 Raw performance data from API for ${ticker}:`, response);
            
            // Handle API response format: {ok: true, data: {...}} or direct data object
            let data = response;
            if (response && typeof response === 'object' && response.ok && response.data) {
                data = response.data;
                console.log(`📦 Extracted performance data for ${ticker}:`, data);
                console.log(`📦 Performance data keys:`, Object.keys(data));
                console.log(`📦 Sample performance fields:`, {
                    open: data.open,
                    prevClose: data.prevClose,
                    perfWeek: data.perfWeek,
                    perf1Y: data.perf1Y,
                    daysRange: data.daysRange,
                    range52Week: data.range52Week
                });
            }
            
            // Normalize fields
            // Helper function to safely get numeric value (preserves 0)
            const getNumericValue = (...values) => {
                for (const val of values) {
                    // Explicitly check for 0 as valid value
                    if (val === 0) {
                        return 0;
                    }
                    // Check for null, undefined, or empty string
                    if (val !== null && val !== undefined && val !== '') {
                        // Try to parse as number
                        const num = typeof val === 'number' ? val : parseFloat(val);
                        // Return if valid number (not NaN)
                        if (!isNaN(num)) {
                            return num;
                        }
                    }
                }
                return null;
            };

            // Helper function to safely get string value
            const getStringValue = (...values) => {
                for (const val of values) {
                    if (val !== null && val !== undefined && val !== '') {
                        return String(val);
                    }
                }
                return null;
            };

            const normalized = {
                prevClose: getNumericValue(data.prevClose, data.previous_close, data.prev_close),
                open: getNumericValue(data.open, data.open_price),
                bid: getNumericValue(data.bid),
                ask: getNumericValue(data.ask),
                daysRange: {
                    low: getNumericValue(data.daysRange?.low, data.days_range?.low, data.day_low),
                    high: getNumericValue(data.daysRange?.high, data.days_range?.high, data.day_high)
                },
                range52Week: {
                    low: getNumericValue(data.range52Week?.low, data.range52week?.low, data.range_52week?.low, data.year_low),
                    high: getNumericValue(data.range52Week?.high, data.range52week?.high, data.range_52week?.high, data.year_high)
                },
                volume: getNumericValue(data.volume),
                avgVolume: getNumericValue(data.avgVolume, data.average_volume, data.avg_volume),
                marketCap: getNumericValue(data.marketCap, data.market_cap),
                sharesOutstanding: getNumericValue(data.sharesOutstanding, data.shares_outstanding),
                beta5YMonthly: getNumericValue(data.beta5YMonthly, data.beta_5y_monthly, data.beta),
                peRatioTTM: getNumericValue(data.peRatioTTM, data.pe_ratio_ttm, data.pe_ratio),
                epsTTM_Diluted: getNumericValue(data.epsTTM_Diluted, data.eps_ttm_diluted, data.eps),
                forwardPERatioNTM: getNumericValue(data.forwardPERatioNTM, data.forward_pe_ratio_ntm),
                forecastEPSNQTR: getNumericValue(data.forecastEPSNQTR, data.forecast_eps_nqtr),
                priceToSalesTTM: getNumericValue(data.priceToSalesTTM, data.price_to_sales_ttm),
                priceToBookMRQ: getNumericValue(data.priceToBookMRQ, data.price_to_book_mrq),
                priceToFreeCashFlowTTM: getNumericValue(data.priceToFreeCashFlowTTM, data.price_to_free_cash_flow_ttm),
                enterpriseValueToRevenueTTM: getNumericValue(data.enterpriseValueToRevenueTTM, data.enterprise_value_to_revenue_ttm),
                enterpriseValueToEBITDA: getNumericValue(data.enterpriseValueToEBITDA, data.enterprise_value_to_ebitda),
                earningsDate: getStringValue(data.earningsDate, data.earnings_date),
                forwardDividendAndYield: data.forwardDividendAndYield || data.forward_dividend_and_yield || null,
                exDividendDate: getStringValue(data.exDividendDate, data.ex_dividend_date),
                nextDividendDate: getStringValue(data.nextDividendDate, data.next_dividend_date),
                recentDividendDate: getStringValue(data.recentDividendDate, data.recent_dividend_date),
                shortInterestRatio: getNumericValue(data.shortInterestRatio, data.short_interest_ratio),
                perfWeek: getNumericValue(data.perfWeek, data.perf_week, data.performance_week),
                perfMonth: getNumericValue(data.perfMonth, data.perf_month, data.performance_month),
                perf6M: getNumericValue(data.perf6M, data.perf_6m, data.performance_6m),
                perfYTD: getNumericValue(data.perfYTD, data.perf_ytd, data.performance_ytd),
                perf1Y: getNumericValue(data.perf1Y, data.perf_1y, data.performance_1y),
                perf5Y: getNumericValue(data.perf5Y, data.perf_5y, data.performance_5y)
            };

            // Cache the normalized data
            this.setCachedData('performance', ticker, normalized);

            console.log(`✅ Stock performance fetched for ${ticker}`);
            console.log(`📊 Normalized performance data for ${ticker}:`, normalized);
            return normalized;
        } catch (err) {
            console.error(`❌ Failed to fetch stock performance for ${ticker}: ${err.message}`);
            return null;
        }
    }

    /**
     * Fetch Financials data (Income Statement, Balance Sheet, Cash Flow)
     * @param {string} ticker - Stock ticker symbol
     * @returns {Promise<Object>} - Normalized financials data with income, balance, and cashflow
     */
    async fetchFinancials(ticker) {
        try {
            // Check cache first
            const cached = this.getCachedData('financials', ticker);
            if (cached) {
                return cached;
            }

            // Try to fetch all financials in one request first
            let incomeData = null;
            let balanceData = null;
            let cashflowData = null;

            try {
                const response = await this.fetchProxyGET('financials-v2', ticker);
                
                console.log('═══════════════════════════════════════════════════════════');
                console.log(`📦 Raw financials data from API for ${ticker}:`);
                console.log('═══════════════════════════════════════════════════════════');
                console.log(JSON.stringify(response, null, 2));
                
                // Handle API response format: {ok: true, data: {...}} or direct data object
                let combinedData = response;
                if (response && typeof response === 'object' && response.ok && response.data) {
                    combinedData = response.data;
                    console.log(`📦 Extracted financials data for ${ticker}:`, combinedData);
                    console.log(`📦 Financials data keys:`, Object.keys(combinedData));
                }
                
                // Log income statement structure from API
                if (combinedData.incomeStatement) {
                    console.log('💰 Income Statement from API:', {
                        hasTTM: !!combinedData.incomeStatement.ttm,
                        hasAnnual: !!combinedData.incomeStatement.annual,
                        hasQuarterly: !!combinedData.incomeStatement.quarterly,
                        annualLength: combinedData.incomeStatement.annual?.length || 0,
                        quarterlyLength: combinedData.incomeStatement.quarterly?.length || 0
                    });
                    if (combinedData.incomeStatement.ttm) {
                        console.log('💰 Income TTM from API:', combinedData.incomeStatement.ttm);
                    }
                    if (combinedData.incomeStatement.annual?.length > 0) {
                        console.log('💰 Income Annual first record from API:', combinedData.incomeStatement.annual[0]);
                    }
                }
                
                // Log balance sheet structure from API
                if (combinedData.balanceSheet) {
                    console.log('🏦 Balance Sheet from API:', {
                        hasLatest: !!combinedData.balanceSheet.latest,
                        hasAnnual: !!combinedData.balanceSheet.annual,
                        hasQuarterly: !!combinedData.balanceSheet.quarterly,
                        annualLength: combinedData.balanceSheet.annual?.length || 0,
                        quarterlyLength: combinedData.balanceSheet.quarterly?.length || 0
                    });
                    if (combinedData.balanceSheet.latest) {
                        console.log('🏦 Balance Latest from API:', combinedData.balanceSheet.latest);
                    }
                }
                
                // Log cash flow structure from API
                if (combinedData.cashFlow) {
                    console.log('💵 Cash Flow from API:', {
                        hasTTM: !!combinedData.cashFlow.ttm,
                        hasAnnual: !!combinedData.cashFlow.annual,
                        hasQuarterly: !!combinedData.cashFlow.quarterly,
                        annualLength: combinedData.cashFlow.annual?.length || 0,
                        quarterlyLength: combinedData.cashFlow.quarterly?.length || 0
                    });
                    if (combinedData.cashFlow.ttm) {
                        console.log('💵 Cash Flow TTM from API:', combinedData.cashFlow.ttm);
                    }
                }
                
                // If API returns combined data
                if (combinedData.income || combinedData.incomeStatement) {
                    incomeData = combinedData.income || combinedData.incomeStatement;
                    console.log(`📊 Income data found for ${ticker}:`, incomeData);
                }
                if (combinedData.balance || combinedData.balanceSheet) {
                    balanceData = combinedData.balance || combinedData.balanceSheet;
                    console.log(`📊 Balance data found for ${ticker}:`, balanceData);
                }
                if (combinedData.cashflow || combinedData.cashFlow) {
                    cashflowData = combinedData.cashflow || combinedData.cashFlow;
                    console.log(`📊 Cashflow data found for ${ticker}:`, cashflowData);
                }
            } catch (err) {
                console.log(`⚠️ Combined financials endpoint not available, trying separate endpoints: ${err.message}`);
            }

            // Normalize Income Statement
            const normalizeIncomeStatement = (data) => {
                if (!data) return { annual: [], quarterly: [], ttm: null };
                
                // Define normalize function first
                const normalizeIncomeRecord = (record) => ({
                    revenue: record.revenue || record.totalRevenue || null,
                    costOfRevenue: record.costOfRevenue || record.cost_of_revenue || record.costOfGoodsSold || null,
                    grossProfit: record.grossProfit || record.gross_profit || null,
                    operatingExpense: record.operatingExpense || record.operating_expense || record.operatingExpenses || record.total_operating_expenses || null,
                    operatingIncome: record.operatingIncome || record.operating_income || record.operatingIncomeLoss || null,
                    nonOperatingIncome: record.nonOperatingIncome || record.non_operating_income || record.nonOperatingIncomeLoss || record.other_income_expense || null,
                    interestIncome: record.interestIncome || record.interest_income || record.interestIncomeLoss || record.netInterestIncome || null,
                    depreciationAmortization: record.depreciationAmortization || record.depreciationAndAmortization || record.depreciation_amortization || record.depreciation || null,
                    pretaxIncome: record.pretaxIncome || record.pretax_income || record.preTaxIncome || record.income_before_income_taxes || null,
                    taxes: record.taxes || record.tax || record.incomeTax || record.income_taxes || null,
                    netIncome: record.netIncome || record.net_income || record.net_income_loss_attributable_common_shareholders || null,
                    eps: record.eps || record.earningsPerShare || record.basic_earnings_per_share || record.diluted_earnings_per_share || null,
                    ebit: record.ebit || null,
                    ebitda: record.ebitda || null,
                    period: record.period || record.date || record.endDate || null
                });
                
                // Handle different API response structures
                const annual = Array.isArray(data.annual) ? data.annual 
                    : Array.isArray(data.annualData) ? data.annualData
                    : Array.isArray(data) && data[0]?.period === 'annual' ? data.filter(d => d.period === 'annual')
                    : [];
                
                const quarterly = Array.isArray(data.quarterly) ? data.quarterly
                    : Array.isArray(data.quarterlyData) ? data.quarterlyData
                    : Array.isArray(data) && data[0]?.period === 'quarterly' ? data.filter(d => d.period === 'quarterly')
                    : [];
                
                // Handle TTM data if present - normalize it
                let ttm = null;
                if (data.ttm) {
                    ttm = normalizeIncomeRecord(data.ttm);
                }

                return {
                    annual: annual.map(normalizeIncomeRecord),
                    quarterly: quarterly.map(normalizeIncomeRecord),
                    ttm: ttm
                };
            };

            // Normalize Balance Sheet
            const normalizeBalanceSheet = (data) => {
                if (!data) return { annual: [], quarterly: [], ttm: null };
                
                const annual = Array.isArray(data.annual) ? data.annual 
                    : Array.isArray(data.annualData) ? data.annualData
                    : Array.isArray(data) && data[0]?.period === 'annual' ? data.filter(d => d.period === 'annual')
                    : [];
                
                const quarterly = Array.isArray(data.quarterly) ? data.quarterly
                    : Array.isArray(data.quarterlyData) ? data.quarterlyData
                    : Array.isArray(data) && data[0]?.period === 'quarterly' ? data.filter(d => d.period === 'quarterly')
                    : [];

                const normalizeBalanceRecord = (record) => ({
                    totalAssets: record.totalAssets || record.total_assets || null,
                    currentAssets: record.currentAssets || record.current_assets || record.total_current_assets || null,
                    cash: record.cash || record.cashAndCashEquivalents || record.cash_and_cash_equivalents || null,
                    nonCurrentAssets: record.nonCurrentAssets || record.non_current_assets || null,
                    totalLiabilities: record.totalLiabilities || record.total_liabilities || null,
                    currentLiabilities: record.currentLiabilities || record.current_liabilities || record.total_current_liabilities || null,
                    nonCurrentLiabilities: record.nonCurrentLiabilities || record.non_current_liabilities || null,
                    totalEquity: record.totalEquity || record.total_equity || null,
                    commonStockEquity: record.commonStockEquity || record.common_stock_equity || record.stockholdersEquity || null,
                    cashAndCashEquivalents: record.cashAndCashEquivalents || record.cash_and_cash_equivalents || null,
                    shortTermInvestments: record.shortTermInvestments || record.short_term_investments || null,
                    accountsReceivable: record.accountsReceivable || record.accounts_receivable || null,
                    inventory: record.inventory || null,
                    longTermInvestments: record.longTermInvestments || record.long_term_investments || null,
                    propertyPlantEquipment: record.propertyPlantEquipment || record.property_plant_equipment || null,
                    goodwill: record.goodwill || null,
                    intangibleAssets: record.intangibleAssets || record.intangible_assets || null,
                    period: record.period || record.date || record.endDate || null
                });

                // Handle latest data if present (for balance sheet)
                let ttm = null;
                if (data.latest) {
                    ttm = normalizeBalanceRecord(data.latest);
                }

                return {
                    annual: annual.map(normalizeBalanceRecord),
                    quarterly: quarterly.map(normalizeBalanceRecord),
                    ttm: ttm
                };
            };

            // Normalize Cash Flow
            const normalizeCashFlow = (data) => {
                if (!data) return { annual: [], quarterly: [], ttm: null };
                
                const annual = Array.isArray(data.annual) ? data.annual 
                    : Array.isArray(data.annualData) ? data.annualData
                    : Array.isArray(data) && data[0]?.period === 'annual' ? data.filter(d => d.period === 'annual')
                    : [];
                
                const quarterly = Array.isArray(data.quarterly) ? data.quarterly
                    : Array.isArray(data.quarterlyData) ? data.quarterlyData
                    : Array.isArray(data) && data[0]?.period === 'quarterly' ? data.filter(d => d.period === 'quarterly')
                    : [];

                const normalizeCashFlowRecord = (record) => ({
                    operatingCashFlow: record.operatingCashFlow || record.operating_cash_flow || record.operatingCashFlowCont || null,
                    investingCashFlow: record.investingCashFlow || record.investing_cash_flow || null,
                    financingCashFlow: record.financingCashFlow || record.financing_cash_flow || null,
                    netCashFlow: record.netCashFlow || record.net_cash_flow || null,
                    freeCashFlow: record.freeCashFlow || record.free_cash_flow  || null,
                    capitalExpenditure: record.capitalExpenditure || record.capital_expenditure  || null,
                    netDividendsPaid: record.netDividendsPaid || record.net_dividends_paid  || null,
                    stockRepurchases: record.stockRepurchases || record.stock_repurchases  || null,
                    debtIssuance: record.debtIssuance || record.debt_issuance  || null,
                    debtRepayment: record.debtRepayment || record.debt_repayment  || null,
                    period: record.period || record.date || record.endDate || null
                });

                // Handle TTM data if present
                let ttm = null;
                if (data.ttm) {
                    ttm = normalizeCashFlowRecord(data.ttm);
                }

                return {
                    annual: annual.map(normalizeCashFlowRecord),
                    quarterly: quarterly.map(normalizeCashFlowRecord),
                    ttm: ttm
                };
            };

            const normalized = {
                income: normalizeIncomeStatement(incomeData),
                balance: normalizeBalanceSheet(balanceData),
                cashflow: normalizeCashFlow(cashflowData)
            };

            console.log('═══════════════════════════════════════════════════════════');
            console.log(`✅ Normalized financials data for ${ticker}:`);
            console.log('═══════════════════════════════════════════════════════════');
            console.log('📊 Normalized structure:', {
                income: {
                    hasAnnual: !!normalized.income.annual,
                    hasQuarterly: !!normalized.income.quarterly,
                    hasTTM: !!normalized.income.ttm,
                    annualLength: normalized.income.annual?.length || 0,
                    quarterlyLength: normalized.income.quarterly?.length || 0,
                    ttmType: typeof normalized.income.ttm,
                    ttmKeys: normalized.income.ttm ? Object.keys(normalized.income.ttm) : []
                },
                balance: {
                    hasAnnual: !!normalized.balance.annual,
                    hasQuarterly: !!normalized.balance.quarterly,
                    hasTTM: !!normalized.balance.ttm,
                    annualLength: normalized.balance.annual?.length || 0,
                    quarterlyLength: normalized.balance.quarterly?.length || 0,
                    ttmType: typeof normalized.balance.ttm,
                    ttmKeys: normalized.balance.ttm ? Object.keys(normalized.balance.ttm) : []
                },
                cashflow: {
                    hasAnnual: !!normalized.cashflow.annual,
                    hasQuarterly: !!normalized.cashflow.quarterly,
                    hasTTM: !!normalized.cashflow.ttm,
                    annualLength: normalized.cashflow.annual?.length || 0,
                    quarterlyLength: normalized.cashflow.quarterly?.length || 0,
                    ttmType: typeof normalized.cashflow.ttm,
                    ttmKeys: normalized.cashflow.ttm ? Object.keys(normalized.cashflow.ttm) : []
                }
            });
            
            if (normalized.income.ttm) {
                console.log('💰 Normalized Income TTM:', normalized.income.ttm);
            }
            if (normalized.balance.ttm) {
                console.log('🏦 Normalized Balance TTM:', normalized.balance.ttm);
            }
            if (normalized.cashflow.ttm) {
                console.log('💵 Normalized Cashflow TTM:', normalized.cashflow.ttm);
            }
            
            if (normalized.income.annual?.length > 0) {
                console.log('💰 Normalized Income Annual first record:', normalized.income.annual[0]);
            }
            
            console.log('═══════════════════════════════════════════════════════════');

            // Cache the normalized data
            this.setCachedData('financials', ticker, normalized);

            console.log(`✅ Financials fetched and normalized for ${ticker}`);
            return normalized;
        } catch (err) {
            console.error(`❌ Failed to fetch financials for ${ticker}: ${err.message}`);
            return {
                income: { annual: [], quarterly: [], ttm: null },
                balance: { annual: [], quarterly: [], ttm: null },
                cashflow: { annual: [], quarterly: [], ttm: null }
            };
        }
    }

    /**
     * Fetch all data for a ticker (profile, performance, financials) and return normalized structure
     * @param {string} ticker - Stock ticker symbol
     * @returns {Promise<Object>} - Combined normalized data structure
     */
    async fetchAllData(ticker) {
        try {
            console.log(`📊 Fetching all data for ${ticker}...`);

            const [profile, performance, financials] = await Promise.allSettled([
                this.fetchCompanyProfile(ticker),
                this.fetchStockPerformance(ticker),
                this.fetchFinancials(ticker)
            ]);

            const result = {
                profile: profile.status === 'fulfilled' ? profile.value : null,
                performance: performance.status === 'fulfilled' ? performance.value : null,
                financials: financials.status === 'fulfilled' ? financials.value : {
                    income: { annual: [], quarterly: [], ttm: null },
                    balance: { annual: [], quarterly: [], ttm: null },
                    cashflow: { annual: [], quarterly: [], ttm: null }
                }
            };

            console.log(`✅ All data fetched for ${ticker}`);
            return result;
        } catch (err) {
            console.error(`❌ Failed to fetch all data for ${ticker}: ${err.message}`);
            return {
                profile: null,
                performance: null,
                financials: {
                    income: { annual: [], quarterly: [], ttm: null },
                    balance: { annual: [], quarterly: [], ttm: null },
                    cashflow: { annual: [], quarterly: [], ttm: null }
                }
            };
        }
    }
}

// Expose to window
window.RightPanelProxyAPIManager = RightPanelProxyAPIManager;

